"use client";

import { useCallback, useMemo } from "react";
import { usePathname } from "next/navigation";
import { CasinoBetStatus, TranslationScopes } from "@finbackoffice/enums";
import { DropdownOption } from "@finbackoffice/site-core";
import DateRangePicker, {
    DateRangePickerReturnObj,
} from "components/base/date-range-picker/DateRangePicker";
import { ISportBetsFilterState, TICKET_STATUS } from "hooks/sportsbook";
import { useDate } from "hooks";
import { RouteNames } from "utils/constants";
import UniversalDeviceDropdown from "components/base/universal-device-dropdown/UniversalDeviceDropdown";
import { ICasinoBetsFilterState } from "../casino/CasinoBetsHistory";
import styles from "./bets-history-tools.module.sass";

export type RangeType = "30_days" | "date_range_picker" | "";

interface IProps<T> {
    filter: T;
    onChange: (val: T) => void;
}

const BetsHistoryTools = <T extends ICasinoBetsFilterState | ISportBetsFilterState>({
    filter,
    onChange,
}: IProps<T>) => {
    const pathname = usePathname();
    const { endOfDay } = useDate();

    const statuses = useMemo((): DropdownOption[] => {
        let result: DropdownOption[];

        if (pathname?.includes(RouteNames.CASINO)) {
            result = [
                {
                    value: "",
                    label: {
                        tid: "account_bets_statusAll",
                        namespace: TranslationScopes.UserAccount,
                    },
                },
                {
                    value: CasinoBetStatus.Win,
                    label: {
                        tid: "account_bets_statusWin",
                        namespace: TranslationScopes.UserAccount,
                    },
                },
                {
                    value: CasinoBetStatus.Loss,
                    label: {
                        tid: "account_bets_statusLoss",
                        namespace: TranslationScopes.UserAccount,
                    },
                },
            ];
        } else {
            result = [
                {
                    value: "",
                    label: {
                        tid: "account_bets_statusAll",
                        namespace: TranslationScopes.UserAccount,
                    },
                },
                {
                    value: TICKET_STATUS.settled,
                    label: {
                        tid: "account_bets_statusSettled",
                        namespace: TranslationScopes.UserAccount,
                    },
                },
                {
                    value: TICKET_STATUS.active,
                    label: {
                        tid: "account_bets_statusOpen",
                        namespace: TranslationScopes.UserAccount,
                    },
                },
            ];
        }

        return result;
    }, [pathname]);

    const updateStatusFilter = useCallback(
        (val: string) => onChange({ ...filter, status: val, search: "" }),
        [filter, onChange],
    );

    const onRangeChangeHandler = (obj: DateRangePickerReturnObj) => {
        const range = { ...obj };

        if (range.to) {
            range.to = endOfDay(range.to);
        }
        onChange({ ...filter, ...range, search: "" });
    };

    return (
        <div className={styles.tools}>
            <UniversalDeviceDropdown
                name="status"
                options={statuses}
                value={filter.status}
                onChange={updateStatusFilter}
                wrapperClassname={styles.dropdown}
                variantStyle="gradient"
            />

            <DateRangePicker
                range={{ from: filter.from, to: filter.to }}
                dateFormat="MMM/dd/yy"
                onChange={onRangeChangeHandler}
            />
        </div>
    );
};
export default BetsHistoryTools;
